import { Injectable, inject } from '@angular/core';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { JobTypeService } from '@app/features/job-types/job-type-service';
import { SalaryScaleUiService } from '@app/features/salary/salary-scale-ui-service';
import { SalaryScaleContractUpdateListItem } from '@app/features/salary/salary-scale.model';
import { SalaryScalesService } from '@app/features/salary/salary-scales-service';
import { Observable, of, switchMap, take, throwError } from 'rxjs';
import { ContractHistorySource, HistoryEntry, HistoryOrigin } from './history-entry.model';

export const REVALUATION_ORIGIN: HistoryOrigin = { key: 'revalorisation', label: 'Revalorisation' };

const euros = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

/**
 * Les revalorisations automatiques du salaire horaire, appliquées à la prise d'effet de nouveaux
 * seuils de la convention collective.
 */
@Injectable()
export class SalaryRevaluationHistorySource implements ContractHistorySource {
  readonly origin = REVALUATION_ORIGIN;

  private readonly jobTypeService = inject(JobTypeService);
  private readonly salaryScalesService = inject(SalaryScalesService);
  private readonly salaryScaleUiService = inject(SalaryScaleUiService);

  entries(contract: ContractListItem): Observable<HistoryEntry[]> {
    return of(
      (contract.salaryScaleContractUpdates ?? []).map((update) => ({
        id: `revalorisation-${update.id}`,
        origin: REVALUATION_ORIGIN,
        at: update.updatedAt,
        title: ['Salaire horaire revalorisé automatiquement'],
        lines: [
          {
            text: [
              `${euros.format(update.previousGrossHourlySalary)} → ${euros.format(update.newGrossHourlySalary)}`,
            ],
          },
          { text: ['Nouveaux seuils de la convention collective'] },
        ],
        action: {
          label: 'Voir la grille de salaire appliquée',
          run: () => this.showSalaryScale(update),
        },
      })),
    );
  }

  private showSalaryScale(update: SalaryScaleContractUpdateListItem): void {
    // Requête unique, et liste des types d'emploi bornée par take(1) : l'abonnement se termine seul.
    this.salaryScalesService
      .findBySalaryScaleEntryId(update.salaryScaleEntryId)
      .pipe(
        switchMap((salaryScale) =>
          this.jobTypeService.allJobTypes$.pipe(
            take(1),
            switchMap((jobTypes) => {
              const jobType = jobTypes.find((jt) => jt.id === salaryScale.jobTypeId);
              return jobType
                ? of({ salaryScale, jobType })
                : throwError(
                    () =>
                      new Error(`Impossible de trouver le type d'emploi pour l'échelle salariale`),
                  );
            }),
          ),
        ),
      )
      .subscribe(({ salaryScale, jobType }) =>
        this.salaryScaleUiService.openEditSalaryScaleDialog(jobType, salaryScale, false),
      );
  }
}
